import { FunctionComponent, ReactNode, useRef } from "react"
import styled from "styled-components"
import { X } from "lucide-react"
import Text from "./Text";
import { styles } from "../styles/themes/style";
import SvgFragment from "../fragments/SvgFragment";

interface IStyledTagProps {
    $color?: string,
    $borderColor?: string,
    $backgroundColor?: string,
    $cutLeft?: boolean,
    $cutRight?: boolean,
    $width?: string,
    $height?: string,
    $isDisconnected?: boolean,
    $hasIcon?: boolean,
    $hasAction?: boolean,
    $inboxConstrained?: boolean,
}

interface ITagProps extends IStyledTagProps {
    title: string,
    SVGtype?: string,
    icon?: ReactNode,
    emoji?: string,
    tooltipMessage?: string,
    className?: string,
    onDestructiveClickHandler?: (e: any) => void
}

const Tag: FunctionComponent<ITagProps> = ({ SVGtype, icon, emoji, title, tooltipMessage, $width, $height, onDestructiveClickHandler, className, ...props }) => {
    const textRef = useRef<HTMLDivElement>(null)
    const hasIcon = !!emoji || !!SVGtype || !!icon
    const inboxConstrained = className?.includes('inbox-conversation-tag')
   
    const isEllipsisApplied = () => {
        const element = textRef.current

        if (element) {
          return element.scrollWidth > element.clientWidth
        }
        return false
    }
    
    return (
        <StyledTagContainer $width={$width} $height={$height} className={className} $hasIcon={hasIcon} $hasAction={!!onDestructiveClickHandler} $inboxConstrained={inboxConstrained} {...props}>   
            {emoji ? (
                <StyledEmoji {...props}>{emoji}</StyledEmoji>
            ) : icon ? (
                <StyledIcon {...props}>{icon}</StyledIcon>
            ) : (
                SVGtype && <StyledIcon {...props}><SvgFragment type={SVGtype} /></StyledIcon>
            )}
            <StyledTag $hasIcon={hasIcon} $hasAction={!!onDestructiveClickHandler} $inboxConstrained={inboxConstrained} {...props}>
                <Text ref={textRef} $paragraph3 $bold $inheritColor $ellipsis="0" showTooltip={isEllipsisApplied()} tooltipMessage={tooltipMessage || title}>
                    {title}
                </Text>
            </StyledTag>
            {onDestructiveClickHandler && <StyledDestructiveAction onClick={(e) => onDestructiveClickHandler(e)} {...props}>
                <X size={14} strokeWidth={3} />
            </StyledDestructiveAction>}
        </StyledTagContainer>

    )
}

const parseHexColor = (color?: string) => {
    if (!color) return null

    const normalized = color.trim().replace('#', '')
    const hex = normalized.length === 3
        ? normalized.split('').map(char => `${char}${char}`).join('')
        : normalized

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null

    return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
    }
}

const getColorLuminance = (color?: string) => {
    const rgb = parseHexColor(color)
    if (!rgb) return 0

    const toLinear = (channel: number) => {
        const value = channel / 255
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
    }

    const r = toLinear(rgb.r)
    const g = toLinear(rgb.g)
    const b = toLinear(rgb.b)

    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const hexToTint = (color?: string) => {
    const rgb = parseHexColor(color)
    if (!rgb) return styles.colors.white

    const amount = 0.92
    const mix = (channel: number) => Math.round(channel * (1 - amount) + 255 * amount)
        .toString(16)
        .padStart(2, '0')

    return `#${mix(rgb.r)}${mix(rgb.g)}${mix(rgb.b)}`
}

/** Status/billing tags pass a light $color as fill; connection tags pass a saturated color to tint. */
const resolveTagBackgroundColor = (backgroundColor?: string, color?: string) => {
    if (backgroundColor) return backgroundColor
    if (!color) return styles.colors.white
    if (getColorLuminance(color) > 0.75) return color
    return hexToTint(color)
}

const StyledIcon = styled.div<IStyledTagProps>`
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${styles.colors.black500};
    svg:not(.svg) {
        width: 12px;
        height: 12px;
        display: block;
        flex-shrink: 0;
    }
    .svg {
        width: 12px;
        height: 12px;
        display: block;
        .path {
            fill: currentColor !important;
        }
        path[stroke], circle[stroke], rect[stroke], line, polyline {
            stroke: currentColor;
        }
        path:not([fill="none"]):not([stroke]), circle:not([fill="none"]):not([stroke]), rect:not([fill="none"]):not([stroke]) {
            fill: currentColor;
        }
    }
`
const StyledEmoji = styled.div<IStyledTagProps>`
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    line-height: 1;
`
const StyledTagContainer = styled.div<IStyledTagProps>`
    max-width: ${({$width}) => ($width !== undefined && $width !== null && $width.length > 0) ? $width : 'fit-content'};
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 2px;
    height: ${({$height}) => ($height !== undefined && $height !== null && $height.length > 0) ? $height : '20px'};
    min-height: 20px;
    border-radius: 12px;
    box-sizing: border-box;
    background-color: ${({ $backgroundColor, $color }) => resolveTagBackgroundColor($backgroundColor, $color)};
    border: 1px solid ${({ $borderColor, $color }) => $borderColor || $color || styles.colors.black200};
    padding: ${({ $hasIcon, $hasAction }) => {
        if ($hasIcon && $hasAction) return '0 6px'
        if ($hasIcon) return '0 8px 0 6px'
        if ($hasAction) return '0 6px 0 8px'
        return '0 8px'
    }};

    &.connection-tags-class svg {
        width: 12px;
        height: 12px;
    }

    ${({ $inboxConstrained }) => $inboxConstrained && `
        max-width: 100%;
        min-width: 0;
    `}
`
const StyledTag = styled.div<IStyledTagProps>`
    display: flex;
    word-wrap: break-word;
    max-width: 100%;
    height: 20px;
    width: fit-content;
    color: ${styles.colors.black500};
    align-items: center;

    ${({ $inboxConstrained }) => $inboxConstrained && `
        min-width: 0;
        flex: 1 1 auto;
        overflow: hidden;
        width: auto;
    `}

    span {
        font-size: 12px;
        line-height: 20px;
        font-weight: 600;
    }
`
const StyledDestructiveAction = styled.div<IStyledTagProps>`
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    color: ${styles.colors.black500};
    & svg {
        height: 14px;
        width: 14px;
    }
    & svg path, & svg line {
        stroke: currentColor;
    }
    &:hover, &:focus{
        opacity: 0.7;
    }
`
export default Tag